import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { client } from '../generated/client.gen';
import { segmentsQueryKey } from './workspace';

export type IntegrationType = 'brevo' | 'ecomail' | 'mailchimp';

export type IntegrationKeyStatus = 'ok' | 'invalid' | 'undecryptable';

export interface Integration {
  id: string;
  type: IntegrationType;
  name: string;
  apiKeyMask: string;
  fromName?: string;
  fromEmail?: string;
  replyTo?: string;
  baseUrl?: string;
  ownerId?: string;
  status: 'active' | 'disabled';
  createdAt: string;
  updatedAt: string;
  keyStatus: IntegrationKeyStatus;
  keyStatusMessage?: string;
  lastCheckAt?: string;
  lastCheckOk?: boolean;
  lastCheckMessage?: string;
}

export interface CreateIntegrationRequest {
  type: IntegrationType;
  name: string;
  apiKey: string;
  fromName?: string;
  fromEmail?: string;
  replyTo?: string;
  baseUrl?: string;
}

export interface UpdateIntegrationRequest {
  name?: string;
  /** Supply to rotate the stored key; omit to keep the current one. */
  apiKey?: string;
  fromName?: string;
  fromEmail?: string;
  replyTo?: string;
  baseUrl?: string;
}

export interface TestIntegrationRequest {
  type: IntegrationType;
  apiKey: string;
  baseUrl?: string;
}

export interface IntegrationTestResult {
  ok: boolean;
  message: string;
  accountName?: string;
  endpoint?: string;
}

export function hasKeyProblem(integration: Integration): boolean {
  return (integration.keyStatus ?? 'ok') !== 'ok';
}

const integrationsQueryKey = ['integrations'] as const;

function useIntegrations() {
  return useQuery({
    queryKey: integrationsQueryKey,
    queryFn: async (): Promise<Integration[]> => {
      try {
        const { data } = await client.instance.get<{ data?: Integration[] } | Integration[]>(
          '/integrations',
        );
        if (Array.isArray(data)) return data;
        return data.data ?? [];
      } catch (e) {
        // 404 means the server boots without INTEGRATIONS_ENCRYPTION_KEY set
        // — surface as empty list so the settings page can render the
        // "configure your encryption key" hint instead of crashing.
        if (isAxiosError(e) && e.response?.status === 404) return [];
        throw e;
      }
    },
  });
}

function useCreateIntegration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateIntegrationRequest): Promise<Integration> => {
      const { data } = await client.instance.post<Integration>('/integrations', body);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: integrationsQueryKey }),
  });
}

function useUpdateIntegration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: UpdateIntegrationRequest;
    }): Promise<Integration> => {
      const { data } = await client.instance.put<Integration>(`/integrations/${id}`, body);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: integrationsQueryKey }),
  });
}

function useDeleteIntegration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.instance.delete(`/integrations/${id}`);
    },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: integrationsQueryKey }),
        queryClient.invalidateQueries({ queryKey: segmentsQueryKey }),
      ]),
  });
}

function useTestIntegration() {
  return useMutation({
    mutationFn: async (body: TestIntegrationRequest): Promise<IntegrationTestResult> => {
      const { data } = await client.instance.post<IntegrationTestResult>(
        '/integrations/test',
        body,
      );
      return data;
    },
  });
}

function useTestStoredIntegration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<IntegrationTestResult> => {
      const { data } = await client.instance.post<IntegrationTestResult>(
        `/integrations/${id}/test`,
      );
      return data;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: integrationsQueryKey }),
  });
}

export const integrationsApi = {
  useList: useIntegrations,
  useCreate: useCreateIntegration,
  useUpdate: useUpdateIntegration,
  useDelete: useDeleteIntegration,
  useTest: useTestIntegration,
  useTestStored: useTestStoredIntegration,
};
